import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  AITutoringSession,
  AdaptiveExplanation,
  ContextualHelp,
  PracticeRecommendation,
  AIGeneratedQuiz,
  TutoringAnalytics,
  TutoringSessionResponse,
  TutoringAnalyticsResponse,
  QuizGenerationRequest,
  QuizGenerationResponse,
} from '@coffeeplexity/shared/src/types/aiTutoring';

interface AITutoringState {
  // Current tutoring session
  currentSession: AITutoringSession | null;
  isSessionActive: boolean;
  sessionLoading: boolean;

  // Adaptive explanations
  currentExplanation: AdaptiveExplanation | null;
  explanationHistory: AdaptiveExplanation[];
  explanationLoading: boolean;

  // Contextual help
  activeHelp: ContextualHelp[];
  helpHistory: ContextualHelp[];
  helpLoading: boolean;

  // Practice recommendations
  practiceRecommendations: PracticeRecommendation[];
  activePractice: PracticeRecommendation | null;
  practiceLoading: boolean;

  // AI-generated quizzes
  currentQuiz: AIGeneratedQuiz | null;
  quizHistory: AIGeneratedQuiz[];
  quizGenerating: boolean;

  // Analytics and effectiveness
  analytics: TutoringAnalytics | null;
  analyticsLoading: boolean;

  // UI state
  showHelpOverlay: boolean;
  selectedHelpTopic: string | null;
  tutorPersonality: 'encouraging' | 'professional' | 'friendly' | 'expert';

  // Error handling
  error: string | null;
  lastUpdated: string | null;
}

const initialState: AITutoringState = {
  currentSession: null,
  isSessionActive: false,
  sessionLoading: false,

  currentExplanation: null,
  explanationHistory: [],
  explanationLoading: false,

  activeHelp: [],
  helpHistory: [],
  helpLoading: false,

  practiceRecommendations: [],
  activePractice: null,
  practiceLoading: false,

  currentQuiz: null,
  quizHistory: [],
  quizGenerating: false,

  analytics: null,
  analyticsLoading: false,

  showHelpOverlay: false,
  selectedHelpTopic: null,
  tutorPersonality: 'encouraging',

  error: null,
  lastUpdated: null,
};

// Async thunks
export const startTutoringSession = createAsyncThunk(
  'aiTutoring/startSession',
  async (params: {
    topicId: string;
    sessionType: AITutoringSession['sessionType'];
  }) => {
    const response = await fetch('/api/ai/tutoring/start-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error('Failed to start tutoring session');
    }

    const data: TutoringSessionResponse = await response.json();
    return data.data;
  }
);

export const endTutoringSession = createAsyncThunk(
  'aiTutoring/endSession',
  async (sessionId: string) => {
    const response = await fetch(`/api/ai/tutoring/end-session/${sessionId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Failed to end tutoring session');
    }

    const data: TutoringSessionResponse = await response.json();
    return data.data;
  }
);

export const getAdaptiveExplanation = createAsyncThunk(
  'aiTutoring/getExplanation',
  async (params: {
    topicId: string;
    currentLevel: string;
    userContext?: any;
  }) => {
    const response = await fetch('/api/ai/tutoring/explanation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error('Failed to get adaptive explanation');
    }

    const data = await response.json();
    return data.data as AdaptiveExplanation;
  }
);

export const requestContextualHelp = createAsyncThunk(
  'aiTutoring/requestHelp',
  async (params: { context: any; helpType: ContextualHelp['helpType'] }) => {
    const response = await fetch('/api/ai/tutoring/contextual-help', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error('Failed to get contextual help');
    }

    const data = await response.json();
    return data.data as ContextualHelp;
  }
);

export const getPracticeRecommendations = createAsyncThunk(
  'aiTutoring/getPracticeRecommendations',
  async (userId: string) => {
    const response = await fetch(
      `/api/ai/tutoring/practice-recommendations/${userId}`
    );

    if (!response.ok) {
      throw new Error('Failed to get practice recommendations');
    }

    const data = await response.json();
    return data.data as PracticeRecommendation[];
  }
);

export const generateQuiz = createAsyncThunk(
  'aiTutoring/generateQuiz',
  async (request: QuizGenerationRequest) => {
    const response = await fetch('/api/ai/tutoring/generate-quiz', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to generate quiz');
    }

    const data: QuizGenerationResponse = await response.json();
    return data.data;
  }
);

export const getTutoringAnalytics = createAsyncThunk(
  'aiTutoring/getAnalytics',
  async (params: {
    userId: string;
    timeframe: TutoringAnalytics['timeframe'];
  }) => {
    const response = await fetch(
      `/api/ai/tutoring/analytics/${params.userId}?timeframe=${params.timeframe}`
    );

    if (!response.ok) {
      throw new Error('Failed to get tutoring analytics');
    }

    const data: TutoringAnalyticsResponse = await response.json();
    return data.data;
  }
);

export const adaptExplanation = createAsyncThunk(
  'aiTutoring/adaptExplanation',
  async (params: {
    explanationId: string;
    adaptationType: string;
    userFeedback: string;
  }) => {
    const response = await fetch('/api/ai/tutoring/adapt-explanation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error('Failed to adapt explanation');
    }

    const data = await response.json();
    return data.data as AdaptiveExplanation;
  }
);

export const submitQuizAnswer = createAsyncThunk(
  'aiTutoring/submitQuizAnswer',
  async (params: {
    quizId: string;
    questionId: string;
    answer: string | string[];
    timeSpent: number;
    confidence?: number;
  }) => {
    const response = await fetch('/api/ai/tutoring/submit-quiz-answer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error('Failed to submit quiz answer');
    }

    const data = await response.json();
    return data.data;
  }
);

const aiTutoringSlice = createSlice({
  name: 'aiTutoring',
  initialState,
  reducers: {
    // Session management
    clearCurrentSession: state => {
      state.currentSession = null;
      state.isSessionActive = false;
    },

    updateSessionProgress: (
      state,
      action: PayloadAction<{ adaptations: any[]; outcomes: any[] }>
    ) => {
      if (state.currentSession) {
        state.currentSession.adaptations = action.payload.adaptations;
        state.currentSession.learningOutcomes = action.payload.outcomes;
      }
    },

    // Explanation management
    clearCurrentExplanation: state => {
      state.currentExplanation = null;
    },

    updateExplanationLevel: (state, action: PayloadAction<number>) => {
      if (state.currentExplanation) {
        state.currentExplanation.currentLevel = action.payload;
      }
    },

    addExplanationToHistory: (
      state,
      action: PayloadAction<AdaptiveExplanation>
    ) => {
      state.explanationHistory.unshift(action.payload);
      // Keep only last 10 explanations
      if (state.explanationHistory.length > 10) {
        state.explanationHistory = state.explanationHistory.slice(0, 10);
      }
    },

    // Help management
    dismissHelp: (state, action: PayloadAction<string>) => {
      state.activeHelp = state.activeHelp.filter(
        help => help.helpId !== action.payload
      );
    },

    clearAllHelp: state => {
      state.activeHelp = [];
    },

    toggleHelpOverlay: state => {
      state.showHelpOverlay = !state.showHelpOverlay;
    },

    setSelectedHelpTopic: (state, action: PayloadAction<string | null>) => {
      state.selectedHelpTopic = action.payload;
    },

    // Practice management
    startPracticeSession: (
      state,
      action: PayloadAction<PracticeRecommendation>
    ) => {
      state.activePractice = action.payload;
    },

    endPracticeSession: state => {
      state.activePractice = null;
    },

    updatePracticeProgress: (state, action: PayloadAction<any>) => {
      if (state.activePractice) {
        // Update practice progress
        state.activePractice = { ...state.activePractice, ...action.payload };
      }
    },

    // Quiz management
    clearCurrentQuiz: state => {
      state.currentQuiz = null;
    },

    updateQuizProgress: (
      state,
      action: PayloadAction<{
        questionId: string;
        answer: any;
        correct: boolean;
      }>
    ) => {
      if (state.currentQuiz) {
        // Update quiz progress logic here
        const question = state.currentQuiz.questions.find(
          q => q.questionId === action.payload.questionId
        );
        if (question) {
          // Add answer tracking logic
        }
      }
    },

    addQuizToHistory: (state, action: PayloadAction<AIGeneratedQuiz>) => {
      state.quizHistory.unshift(action.payload);
      // Keep only last 5 quizzes
      if (state.quizHistory.length > 5) {
        state.quizHistory = state.quizHistory.slice(0, 5);
      }
    },

    // UI state management
    setTutorPersonality: (
      state,
      action: PayloadAction<AITutoringState['tutorPersonality']>
    ) => {
      state.tutorPersonality = action.payload;
    },

    // Error handling
    clearError: state => {
      state.error = null;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
  extraReducers: builder => {
    // Start tutoring session
    builder
      .addCase(startTutoringSession.pending, state => {
        state.sessionLoading = true;
        state.error = null;
      })
      .addCase(startTutoringSession.fulfilled, (state, action) => {
        state.sessionLoading = false;
        state.currentSession = action.payload;
        state.isSessionActive = true;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(startTutoringSession.rejected, (state, action) => {
        state.sessionLoading = false;
        state.error =
          action.error.message || 'Failed to start tutoring session';
      });

    // End tutoring session
    builder
      .addCase(endTutoringSession.pending, state => {
        state.sessionLoading = true;
      })
      .addCase(endTutoringSession.fulfilled, (state, action) => {
        state.sessionLoading = false;
        state.currentSession = action.payload;
        state.isSessionActive = false;
      })
      .addCase(endTutoringSession.rejected, (state, action) => {
        state.sessionLoading = false;
        state.error = action.error.message || 'Failed to end tutoring session';
      });

    // Get adaptive explanation
    builder
      .addCase(getAdaptiveExplanation.pending, state => {
        state.explanationLoading = true;
        state.error = null;
      })
      .addCase(getAdaptiveExplanation.fulfilled, (state, action) => {
        state.explanationLoading = false;
        state.currentExplanation = action.payload;
        state.explanationHistory.unshift(action.payload);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getAdaptiveExplanation.rejected, (state, action) => {
        state.explanationLoading = false;
        state.error = action.error.message || 'Failed to get explanation';
      });

    // Request contextual help
    builder
      .addCase(requestContextualHelp.pending, state => {
        state.helpLoading = true;
        state.error = null;
      })
      .addCase(requestContextualHelp.fulfilled, (state, action) => {
        state.helpLoading = false;
        state.activeHelp.push(action.payload);
        state.helpHistory.unshift(action.payload);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(requestContextualHelp.rejected, (state, action) => {
        state.helpLoading = false;
        state.error = action.error.message || 'Failed to get help';
      });

    // Get practice recommendations
    builder
      .addCase(getPracticeRecommendations.pending, state => {
        state.practiceLoading = true;
        state.error = null;
      })
      .addCase(getPracticeRecommendations.fulfilled, (state, action) => {
        state.practiceLoading = false;
        state.practiceRecommendations = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getPracticeRecommendations.rejected, (state, action) => {
        state.practiceLoading = false;
        state.error =
          action.error.message || 'Failed to get practice recommendations';
      });

    // Generate quiz
    builder
      .addCase(generateQuiz.pending, state => {
        state.quizGenerating = true;
        state.error = null;
      })
      .addCase(generateQuiz.fulfilled, (state, action) => {
        state.quizGenerating = false;
        state.currentQuiz = action.payload;
        state.quizHistory.unshift(action.payload);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(generateQuiz.rejected, (state, action) => {
        state.quizGenerating = false;
        state.error = action.error.message || 'Failed to generate quiz';
      });

    // Get tutoring analytics
    builder
      .addCase(getTutoringAnalytics.pending, state => {
        state.analyticsLoading = true;
        state.error = null;
      })
      .addCase(getTutoringAnalytics.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analytics = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getTutoringAnalytics.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.error.message || 'Failed to get analytics';
      });

    // Adapt explanation
    builder
      .addCase(adaptExplanation.pending, state => {
        state.explanationLoading = true;
      })
      .addCase(adaptExplanation.fulfilled, (state, action) => {
        state.explanationLoading = false;
        state.currentExplanation = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(adaptExplanation.rejected, (state, action) => {
        state.explanationLoading = false;
        state.error = action.error.message || 'Failed to adapt explanation';
      });

    // Submit quiz answer
    builder
      .addCase(submitQuizAnswer.pending, () => {
        // Could add loading state for individual questions
      })
      .addCase(submitQuizAnswer.fulfilled, state => {
        // Update quiz state with answer results
        if (state.currentQuiz) {
          // Update quiz progress based on response
        }
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(submitQuizAnswer.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to submit quiz answer';
      });
  },
});

export const {
  clearCurrentSession,
  updateSessionProgress,
  clearCurrentExplanation,
  updateExplanationLevel,
  addExplanationToHistory,
  dismissHelp,
  clearAllHelp,
  toggleHelpOverlay,
  setSelectedHelpTopic,
  startPracticeSession,
  endPracticeSession,
  updatePracticeProgress,
  clearCurrentQuiz,
  updateQuizProgress,
  addQuizToHistory,
  setTutorPersonality,
  clearError,
  setError,
} = aiTutoringSlice.actions;

export default aiTutoringSlice.reducer;

// Selectors
export const selectCurrentTutoringSession = (state: {
  aiTutoring: AITutoringState;
}) => state.aiTutoring.currentSession;

export const selectIsSessionActive = (state: { aiTutoring: AITutoringState }) =>
  state.aiTutoring.isSessionActive;

export const selectCurrentExplanation = (state: {
  aiTutoring: AITutoringState;
}) => state.aiTutoring.currentExplanation;

export const selectActiveHelp = (state: { aiTutoring: AITutoringState }) =>
  state.aiTutoring.activeHelp;

export const selectPracticeRecommendations = (state: {
  aiTutoring: AITutoringState;
}) => state.aiTutoring.practiceRecommendations;

export const selectCurrentQuiz = (state: { aiTutoring: AITutoringState }) =>
  state.aiTutoring.currentQuiz;

export const selectTutoringAnalytics = (state: {
  aiTutoring: AITutoringState;
}) => state.aiTutoring.analytics;

export const selectTutoringError = (state: { aiTutoring: AITutoringState }) =>
  state.aiTutoring.error;

export const selectTutoringLoading = (state: {
  aiTutoring: AITutoringState;
}) => ({
  session: state.aiTutoring.sessionLoading,
  explanation: state.aiTutoring.explanationLoading,
  help: state.aiTutoring.helpLoading,
  practice: state.aiTutoring.practiceLoading,
  quiz: state.aiTutoring.quizGenerating,
  analytics: state.aiTutoring.analyticsLoading,
});
